'use client';

import { useState, useEffect, useCallback } from 'react';
import { SHA256 } from 'crypto-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Upload, X, Sparkles, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sha256HashContent } from '../../data/sha256-hash-content';
import Link from 'next/link';

const sampleText = 'Hello, world! This is a sample text for SHA-256 hashing.';
const sampleHash = 'dffd6021bb2bd5b0af676290809ec3a53191dd81c7f70a4b28688a362182986f';

export default function SHA256HashTool() {
    const [input, setInput] = useState('');
    const [hash, setHash] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const { toast } = useToast();

    // Generate SHA-256 hash when input changes
    const generateHash = useCallback((text: string) => {
        if (!text.trim()) {
            setHash('');
            return;
        }

        try {
            setIsLoading(true);
            const hash = SHA256(text).toString();
            setHash(hash);
        } catch (error) {
            console.error('Error generating SHA-256 hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate SHA-256 hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        generateHash(input);
    }, [input, generateHash]);

    const handleCopy = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash)
            .then(() => {
                setIsCopied(true);
                toast({
                    title: 'Success',
                    description: 'Hash copied to clipboard!',
                });
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch(() => {
                toast({
                    title: 'Error',
                    description: 'Failed to copy to clipboard',
                    variant: 'destructive',
                });
            });
    };

    const handleClear = () => {
        setInput('');
        setFileName(null);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.onerror = () => {
            toast({
                title: 'Error',
                description: 'Failed to read file',
                variant: 'destructive',
            });
        };
        reader.readAsText(file);
    };

    const handleUseSample = () => {
        setInput(sampleText);
        setFileName(null);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <Toaster />
            <div className="max-w-4xl mx-auto space-y-6">
                <ToolPageIntro title="SHA-256 Hash Generator">
                    Generate SHA-256 hashes from text or files. Use it for checksums, integrity verification, or digital signatures. Paste or upload; results update instantly. Cryptographically strong and widely used.
                </ToolPageIntro>

                {/* Main content */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Input Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>Input</CardTitle>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleUseSample}
                                        disabled={isLoading}
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input || isLoading}
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <CardDescription>
                                {fileName ? (
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <FileText className="h-4 w-4 mr-2" />
                                        {fileName}
                                    </div>
                                ) : (
                                    'Enter text or upload a file to generate SHA-256 hash'
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="relative">
                                    <Textarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Enter text to hash..."
                                        className="min-h-[200px] font-mono text-sm"
                                        disabled={isLoading}
                                    />
                                    <div className="absolute right-2 bottom-2">
                                        <input
                                            type="file"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            id="file-upload"
                                            disabled={isLoading}
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            asChild
                                            className="h-8"
                                            disabled={isLoading}
                                        >
                                            <label htmlFor="file-upload" className="cursor-pointer">
                                                <Upload className="h-4 w-4 mr-2" />
                                                {fileName ? 'Change File' : 'Upload File'}
                                            </label>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Output Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>SHA-256 Hash</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!hash || isLoading}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                            <CardDescription>
                                {hash ? 'Your SHA-256 hash is ready' : 'Hash will appear here'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className={cn(
                                'relative rounded-md border p-4 font-mono text-sm overflow-x-auto',
                                'min-h-[200px] flex items-center justify-center',
                                hash ? 'bg-muted/50' : 'bg-muted/20',
                                isLoading && 'opacity-70'
                            )}>
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center space-y-2 text-muted-foreground">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        <p>Generating hash...</p>
                                    </div>
                                ) : hash ? (
                                    <pre className="whitespace-pre-wrap break-all">{hash}</pre>
                                ) : (
                                    <p className="text-muted-foreground text-center">
                                        {input ? 'Processing...' : 'Enter text or upload a file to generate SHA-256 hash'}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Information Section */}
                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle>About SHA-256 Hash</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                            <p>
                                The SHA-256 (Secure Hash Algorithm 256-bit) is a member of the SHA-2 cryptographic hash functions designed by the NSA.
                                It produces a 256-bit (32-byte) hash value, typically rendered as a 64-character hexadecimal number.
                                SHA-256 is one of the most secure hashing functions in the SHA-2 family.
                            </p>
                            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4 my-4 rounded-r">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-green-700 dark:text-green-300">
                                            <strong>Security Note:</strong> SHA-256 is currently considered secure and is widely used in security protocols and applications,
                                            including TLS/SSL, PGP, SSH, and Bitcoin. It provides strong collision resistance and is recommended for most cryptographic applications.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium mb-2">Common Uses</h3>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Blockchain and cryptocurrencies
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Digital signatures
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Password hashing (with salt)
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    File integrity verification
                                </li>
                            </ul>
                        </div>

                        <div className="mt-4 p-4 bg-muted/50 rounded-md">
                            <h4 className="text-sm font-medium mb-2">Example</h4>
                            <div className="space-y-2">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Input:</p>
                                    <pre className="bg-background p-2 rounded text-xs overflow-x-auto">{sampleText}</pre>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">SHA-256 Hash:</p>
                                    <pre className="bg-background p-2 rounded text-xs overflow-x-auto">{sampleHash}</pre>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={sha256HashContent.title}
                    description={sha256HashContent.description}
                    content={sha256HashContent.content}
                    faqs={sha256HashContent.faqs}
                    relatedTools={sha256HashContent.relatedTools}
                />
            </div>
        </div>
    );
}
